import { Add as AddIcon, FolderOpenOutlined, GroupOutlined, MedicalServicesOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { landingLinkFor, patientDetailLinkFor } from '../../../library/constants';
import { parseApiError } from '../../../library/apiErrorHelper';
import { memberFullName, STATUS_COLOURS, STATUS_LABELS } from '../../../library/membershipDisplay';
import { IClinicMember } from '../../../library/models/clinic';
import {
    ITherapistRevenueShare,
    sharesOf,
} from '../../../library/models/therapistRevenueShare';
import { InviteTherapistFormValues, toInviteTherapistRequest } from '../../../library/models/clinicAdmin';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { enterImpersonation } from '../../../services/sessionKeeper';
import { services } from '../../../services/services';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import { actionsCellStyles, actionsHeaderCellStyles, alertStyles, emptyStateBoxStyles, emptyStateIconStyles, footnoteStyles, headerBoxStyles, headerCellStyles, identifierCellStyles, loadingBoxStyles, rowActionsBoxStyles, rowStyles, subtitleStyles, tableContainerStyles, tabsStyles, titleStyles } from './clinicAdmin.styles';
import { BrandingSettings } from './BrandingSettings';
import { HomeSettings } from './HomeSettings';
import { InvoicingSettings } from './InvoicingSettings';
import { ReminderSettings } from './ReminderSettings';
import { RoomsSettings } from './RoomsSettings';
import { WorkingHoursSettings } from './WorkingHoursSettings';
import { InviteTherapistDialog } from './InviteTherapistDialog';
import { TherapistServicesDialog } from './TherapistServicesDialog';
import { TherapistScheduleDialog } from './TherapistScheduleDialog';
import { TherapistShareTrigger } from './TherapistShareTrigger';
import { TherapistSharePopover } from './TherapistSharePopover';
import { PersonLabel } from '../../userAvatar/UserAvatar';

/**
 * What another page may pass in router state to land on a tab other than the first -- the
 * patient's page does, so "back" from a file opened here returns to the Patients tab.
 */
export interface IClinicAdminLocationState {
    tab?: TabKey;
}

type TabKey =
    | 'THERAPISTS'
    | 'PATIENTS'
    | 'WORKING_HOURS'
    | 'ROOMS'
    | 'BRANDING'
    | 'HOME'
    | 'REMINDERS'
    | 'INVOICING';

/** Which member a confirmation is about, and which way it goes. */
interface PendingAction {
    member: IClinicMember;
    kind: 'revoke' | 'reinvite';
}

/**
 * A clinic administrator's console: the clinic's therapists, and its patients.
 *
 * Therapists are managed here; patients are read-only, because a therapist onboards
 * their own. Both lists include people who were invited and have not accepted, and
 * people whose access was taken away -- an admin who cannot see those has no feedback
 * that an invitation was sent, and no way to bring a former colleague back.
 *
 * Names are not editable, deliberately. One account is shared by every clinic a person
 * belongs to, so renaming somebody here would change how they appear everywhere else;
 * the API refuses it and the footnote below the table says so.
 */
export const ClinicAdmin = (): JSX.Element => {
    const theme = useTheme();
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();

    const navigate = useNavigate();
    const location = useLocation();

    const [tab, setTab] = useState<TabKey>(
        (location.state as IClinicAdminLocationState | null)?.tab ?? 'THERAPISTS'
    );
    const [isInviteOpen, setIsInviteOpen] = useState(false);
    /** Whose hours are being looked at, or null. Looked at only -- see TherapistScheduleDialog. */
    const [viewingScheduleFor, setViewingScheduleFor] = useState<IClinicMember | null>(null);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    /** Whose services are being edited, or null when that dialog is closed. */
    const [editingServicesFor, setEditingServicesFor] = useState<IClinicMember | null>(null);
    /**
     * Whose share is being edited, and the element the popover hangs off. One object rather than
     * two pieces of state: an anchor without a member, or the reverse, is not a state this page
     * has.
     */
    const [editingShare, setEditingShare] =
        useState<{ member: IClinicMember; anchor: HTMLElement } | null>(null);

    // Both lists are fetched up front rather than on tab change: they are two small
    // reads of the same table, and paying for them once keeps switching tabs instant.
    const {
        data: therapistsData,
        isLoading: isLoadingTherapists,
        refetch: refetchTherapists,
    } = useReactQuery<IClinicMember[]>(services.users.getClinicMembers('THERAPIST'));

    const {
        data: patientsData,
        isLoading: isLoadingPatients,
    } = useReactQuery<IClinicMember[]>(services.users.getClinicMembers('CUSTOMER'));

    // One read for the whole clinic rather than one per row. Therapists with no rate are simply
    // absent from it, which `sharesOf` reads as "keeps the whole income".
    const {
        data: revenueSharesData,
        refetch: refetchRevenueShares,
    } = useReactQuery<ITherapistRevenueShare[]>(services.therapists.getRevenueShares());

    const inviteTherapist = useReactMutation(services.users.inviteTherapistMutation());
    const revokeMember = useReactMutation(services.users.revokeMemberMutation());
    const impersonate = useReactMutation(services.authService.impersonateMutation());

    /**
     * Opens this tab as the therapist, for an hour; the header says so and has the way back.
     * No confirmation: nothing has happened until the administrator acts, and going back is
     * one click.
     */
    const actAs = (member: IClinicMember): void => {
        impersonate.mutate(member.id, {
            onSuccess: (session) => enterImpersonation(session, landingLinkFor(session.role)),
            onError: (error: unknown) => {
                const parsed = parseApiError(error);
                showSnackbar(parsed.message ?? translate(ResourcesIds.IMPERSONATION_START_FAILED), 'error');
            },
        });
    };

    /**
     * Whether the list was actually fetched, which is not the same as "isError is false".
     * A query can also settle without data and without an error -- React Query pauses one
     * it believes cannot reach the network -- and treating that as an empty result would
     * tell an admin their clinic has no therapists when nothing was ever loaded.
     */
    const hasLoadedTherapists = therapistsData !== undefined;
    const hasLoadedPatients = patientsData !== undefined;
    const therapists = therapistsData ?? [];
    const patients = patientsData ?? [];

    /**
     * Rejects on failure rather than handling it: the dialog owns the mapping from an API
     * error back onto its own fields, and only a success closes it. On success the list is
     * refetched so the new invitation shows up as pending straight away -- which is the
     * only feedback that it was sent.
     */
    const submitInvite = async (values: InviteTherapistFormValues): Promise<void> => {
        await inviteTherapist.mutateAsync(toInviteTherapistRequest(values));

        showSnackbar(
            translate(ResourcesIds.INVITE_THERAPIST_SUCCESS, { emailAddress: values.emailAddress.trim() }),
            'success'
        );
        setIsInviteOpen(false);
        refetchTherapists();
    };

    const confirmPendingAction = (): void => {
        if (!pendingAction) {
            return;
        }

        const { member, kind } = pendingAction;
        setPendingAction(null);

        const onError = (error: unknown): void => {
            const parsed = parseApiError(error);
            showSnackbar(parsed.message ?? translate(ResourcesIds.REVOKE_THERAPIST_ERROR), 'error');
        };

        if (kind === 'revoke') {
            revokeMember.mutate(member.id, {
                onSuccess: () => {
                    showSnackbar(translate(ResourcesIds.REVOKE_THERAPIST_SUCCESS), 'success');
                    refetchTherapists();
                },
                onError,
            });
            return;
        }

        // Re-inviting is the same call as inviting: the server reinstates the membership
        // it already has rather than creating a second one, and emails a fresh link.
        inviteTherapist.mutate(
            toInviteTherapistRequest({
                emailAddress: member.emailAddress,
                firstName: member.firstName ?? '',
                lastName: member.lastName ?? '',
            }),
            {
                onSuccess: () => {
                    showSnackbar(
                        translate(ResourcesIds.INVITE_THERAPIST_SUCCESS, { emailAddress: member.emailAddress }),
                        'success'
                    );
                    refetchTherapists();
                },
                onError: (error: unknown) => {
                    const parsed = parseApiError(error);
                    showSnackbar(parsed.message ?? translate(ResourcesIds.INVITE_THERAPIST_ERROR), 'error');
                },
            }
        );
    };

    const statusChipFor = (member: IClinicMember): JSX.Element => (
        <Chip
            size="small"
            color={STATUS_COLOURS[member.status]}
            label={translate(STATUS_LABELS[member.status])}
        />
    );

    /*
     * What can be done to a member, drawn the same way in the table and on the card. One
     * definition, because a revoke offered in one place and missing in the other would be
     * a bug rather than a layout difference.
     */
    const actionsFor = (member: IClinicMember): JSX.Element =>
        member.status === 'ACTIVE' ? (
            <>
                {/* Only for an active member: assigning services to somebody with no
                    access would have no effect the wizard could ever act on. */}
                <Button size="small" onClick={() => setEditingServicesFor(member)}>
                    {translate(ResourcesIds.THERAPIST_SERVICES_ACTION)}
                </Button>
                {/* Read-only, and only for an active member: a therapist sets their own
                    hours, and what an administrator controls is the ceiling -- which they
                    move from the Working hours tab, for everybody at once. */}
                <Button size="small" onClick={() => setViewingScheduleFor(member)}>
                    {translate(ResourcesIds.THERAPIST_SCHEDULE_ACTION)}
                </Button>
                {/* A therapist and nothing more: the role here is the most privileged one,
                    so an administrator who also treats patients -- this one included -- is
                    never offered, matching what the API would refuse. */}
                {member.role === 'THERAPIST' && (
                    <Button size="small" disabled={impersonate.isPending} onClick={() => actAs(member)}>
                        {translate(ResourcesIds.ACT_AS_THERAPIST_ACTION)}
                    </Button>
                )}
                <Button
                    size="small"
                    color="error"
                    onClick={() => setPendingAction({ member, kind: 'revoke' })}
                >
                    {translate(ResourcesIds.REVOKE_THERAPIST_ACTION)}
                </Button>
            </>
        ) : (
            <Button
                size="small"
                onClick={() => setPendingAction({ member, kind: 'reinvite' })}
            >
                {translate(ResourcesIds.REINVITE_THERAPIST_ACTION)}
            </Button>
        );

    /**
     * The editable share for one member.
     *
     * Shared by the table and the compact card so the two cannot behave differently -- the same
     * argument that kept the label in one place, now with more at stake, since this one opens the
     * editor. A member whose access has lapsed keeps their rate on screen but cannot be edited:
     * setting a rate for somebody who gives no sessions would be configuring nothing.
     */
    const shareCellFor = (member: IClinicMember): JSX.Element => (
        <TherapistShareTrigger
            therapistName={memberFullName(member)}
            shares={sharesOf(revenueSharesData, member.id)}
            isEditable={member.status === 'ACTIVE'}
            onOpen={anchor => setEditingShare({ member, anchor })}
        />
    );

    const renderMembers = (
        members: IClinicMember[],
        isLoading: boolean,
        hasLoaded: boolean,
        copy: { loadError: string; emptyTitle: string; emptySubtitle: string },
        emptyIcon: JSX.Element,
        withActions: boolean,
        openAction?: (member: IClinicMember) => JSX.Element
    ): JSX.Element => {
        // The therapists' row carries the management actions; the patients' carries only a
        // way into the patient's file. Never both, so one column serves either list.
        const rowActions = (member: IClinicMember): JSX.Element | undefined =>
            withActions ? actionsFor(member) : openAction?.(member);
        const hasRowActions = withActions || openAction !== undefined;

        return (
        <>
            {!isLoading && !hasLoaded && (
                <Alert severity="error" sx={alertStyles}>{translate(copy.loadError)}</Alert>
            )}

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={60} />
                </Box>
            ) : !hasLoaded ? null : members.length === 0 ? (
                <Box sx={emptyStateBoxStyles}>
                    {emptyIcon}
                    <Typography variant="h6" gutterBottom>{translate(copy.emptyTitle)}</Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(copy.emptySubtitle)}
                    </Typography>
                </Box>
            ) : isCompact ? (
                <Box sx={cardListStyles}>
                    {members.map(member => (
                        <RecordCard
                            key={member.id}
                            title={<PersonLabel person={member} />}
                            titleAdornment={statusChipFor(member)}
                            isMuted={member.status !== 'ACTIVE'}
                            fields={
                                withActions
                                    ? [
                                        {
                                            label: translate(ResourcesIds.EMAIL_LABEL),
                                            value: member.emailAddress,
                                        },
                                        {
                                            label: translate(ResourcesIds.REVENUE_SHARE_COLUMN),
                                            value: shareCellFor(member),
                                        },
                                    ]
                                    : [
                                        {
                                            label: translate(ResourcesIds.EMAIL_LABEL),
                                            value: member.emailAddress,
                                        },
                                    ]
                            }
                            actions={rowActions(member)}
                        />
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={tableContainerStyles}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEMBER_NAME_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.EMAIL_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEMBER_STATUS_LABEL)}
                                </TableCell>
                                {withActions && (
                                    <TableCell sx={headerCellStyles}>
                                        {translate(ResourcesIds.REVENUE_SHARE_COLUMN)}
                                    </TableCell>
                                )}
                                {hasRowActions && (
                                    <TableCell sx={actionsHeaderCellStyles}>
                                        {translate(ResourcesIds.MEMBER_ACTIONS_LABEL)}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {members.map(member => (
                                <TableRow key={member.id} sx={rowStyles(member.status === 'ACTIVE')}>
                                    <TableCell><PersonLabel person={member} /></TableCell>
                                    <TableCell sx={identifierCellStyles}>{member.emailAddress}</TableCell>
                                    <TableCell>{statusChipFor(member)}</TableCell>
                                    {withActions && (
                                        <TableCell>{shareCellFor(member)}</TableCell>
                                    )}
                                    {hasRowActions && (
                                        <TableCell sx={actionsCellStyles}>
                                            <Box sx={rowActionsBoxStyles}>{rowActions(member)}</Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
        );
    };

    const isReinviting = pendingAction?.kind === 'reinvite';

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Box>
                    <Typography variant="h4" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.CLINIC_ADMIN_TITLE)}
                    </Typography>
                    <Typography variant="body1" sx={subtitleStyles}>
                        {translate(ResourcesIds.CLINIC_ADMIN_SUBTITLE)}
                    </Typography>
                </Box>

                {tab === 'THERAPISTS' && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsInviteOpen(true)}>
                        {translate(ResourcesIds.INVITE_THERAPIST_ACTION)}
                    </Button>
                )}
            </Box>

            {/* Scrollable from the fifth tab on: five Romanian labels do not fit a 375px
                phone, and without this MUI shrinks them until they wrap mid-word. */}
            <Tabs
                value={tab}
                onChange={(_event, next: TabKey) => setTab(next)}
                sx={tabsStyles}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label={translate(ResourcesIds.CLINIC_ADMIN_TITLE)}
            >
                <Tab value="THERAPISTS" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_THERAPISTS)} />
                <Tab value="PATIENTS" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_PATIENTS)} />
                <Tab
                    value="WORKING_HOURS"
                    label={translate(ResourcesIds.CLINIC_ADMIN_TAB_WORKING_HOURS)}
                />
                {/* Beside Working hours: both decide which hours are offered. */}
                <Tab value="ROOMS" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_ROOMS)} />
                <Tab value="BRANDING" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_BRANDING)} />
                {/* Beside Branding rather than after Invoicing: the two are the same question
                    asked twice -- what the clinic looks like, and what it says. */}
                <Tab value="HOME" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_HOME)} />
                <Tab value="REMINDERS" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_REMINDERS)} />
                <Tab value="INVOICING" label={translate(ResourcesIds.CLINIC_ADMIN_TAB_INVOICING)} />
            </Tabs>

            {/* One branch per tab rather than a ternary: with three of them, "not
                therapists" stopped meaning "patients". */}
            {tab === 'THERAPISTS' && (
                <>
                    {renderMembers(
                        therapists,
                        isLoadingTherapists,
                        hasLoadedTherapists,
                        {
                            loadError: ResourcesIds.THERAPISTS_LOAD_ERROR,
                            emptyTitle: ResourcesIds.THERAPISTS_EMPTY_TITLE,
                            emptySubtitle: ResourcesIds.THERAPISTS_EMPTY_SUBTITLE,
                        },
                        <MedicalServicesOutlined sx={emptyStateIconStyles(theme)} />,
                        true
                    )}

                    <Typography variant="caption" sx={footnoteStyles}>
                        {translate(ResourcesIds.PROFILE_EDITED_ELSEWHERE)}
                    </Typography>

                    {/* Stated before an admin types rather than sprung on them afterwards: a new
                        rate deliberately leaves the current month alone, so a save that reported
                        nothing would look like a save that failed. Inside the therapists branch,
                        so the patients tab never carries it. */}
                    <Typography variant="caption" sx={footnoteStyles}>
                        {translate(ResourcesIds.REVENUE_SHARE_FOOTNOTE)}
                    </Typography>
                </>
            )}

            {tab === 'PATIENTS' && renderMembers(
                patients,
                isLoadingPatients,
                hasLoadedPatients,
                {
                    loadError: ResourcesIds.PATIENTS_LOAD_ERROR,
                    emptyTitle: ResourcesIds.PATIENTS_EMPTY_TITLE,
                    emptySubtitle: ResourcesIds.PATIENTS_EMPTY_SUBTITLE,
                },
                <GroupOutlined sx={emptyStateIconStyles(theme)} />,
                false,
                // Into the patient's own file, where an administrator can delete an
                // evaluation that should not be there.
                member => (
                    <Button
                        size="small"
                        startIcon={<FolderOpenOutlined />}
                        onClick={() => navigate(patientDetailLinkFor(member.id))}
                    >
                        {translate(ResourcesIds.PATIENTS_OPEN)}
                    </Button>
                )
            )}

            {tab === 'WORKING_HOURS' && <WorkingHoursSettings />}

            {tab === 'ROOMS' && <RoomsSettings />}

            {tab === 'BRANDING' && <BrandingSettings />}

            {tab === 'HOME' && <HomeSettings />}

            {tab === 'REMINDERS' && <ReminderSettings />}

            {tab === 'INVOICING' && <InvoicingSettings />}

            <InviteTherapistDialog
                isOpen={isInviteOpen}
                onClose={() => setIsInviteOpen(false)}
                onSubmit={submitInvite}
            />

            <TherapistServicesDialog
                therapist={editingServicesFor}
                onClose={() => setEditingServicesFor(null)}
            />

            <TherapistScheduleDialog
                therapist={viewingScheduleFor}
                onClose={() => setViewingScheduleFor(null)}
            />

            {/* Mounted only while it is open, and keyed to the therapist: the editor seeds itself
                from its own defaults, and a fresh mount is what makes react-hook-form validate
                them. Kept mounted and reset instead, it opened with Save disabled over a rate
                that was perfectly good. */}
            {editingShare && (
                <TherapistSharePopover
                    key={editingShare.member.id}
                    anchor={editingShare.anchor}
                    therapistId={editingShare.member.id}
                    therapistName={memberFullName(editingShare.member)}
                    shares={sharesOf(revenueSharesData, editingShare.member.id)}
                    onClose={() => setEditingShare(null)}
                    onSaved={refetchRevenueShares}
                />
            )}

            <ConfirmationDialog
                isOpen={pendingAction !== null}
                onClose={() => setPendingAction(null)}
                onConfirm={confirmPendingAction}
                title={translate(isReinviting ? ResourcesIds.REINVITE_THERAPIST_TITLE : ResourcesIds.REVOKE_THERAPIST_TITLE)}
                description={translate(isReinviting ? ResourcesIds.REINVITE_THERAPIST_DESCRIPTION : ResourcesIds.REVOKE_THERAPIST_DESCRIPTION)}
                confirmMessage={translate(isReinviting ? ResourcesIds.REINVITE_THERAPIST_CONFIRM : ResourcesIds.REVOKE_THERAPIST_CONFIRM)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
